import { GitCommit } from 'lucide-react';
import { CHANGELOG, CATEGORIES } from './data';
import type { CategoryKey } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(isoDate: string): string {
  return new Date(isoDate + 'T12:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const CATEGORY_MAP = Object.fromEntries(
  CATEGORIES.map((c) => [c.key, c])
) as Record<CategoryKey, (typeof CATEGORIES)[number]>;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TimelineProps {
  activeFilter: CategoryKey | null;
}

export function Timeline({ activeFilter }: TimelineProps) {
  const visibleEntries = CHANGELOG.map((entry) => {
    const visibleItems = activeFilter
      ? entry.items.filter((item) => item.category === activeFilter)
      : entry.items;
    return { ...entry, visibleItems };
  }).filter((entry) => entry.visibleItems.length > 0);

  if (visibleEntries.length === 0) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        No entries match the selected filter.
      </p>
    );
  }

  return (
    <div className="relative">
      {/* Vertical connector line */}
      <div className="absolute bottom-2 left-[7px] top-2 w-px bg-border" />

      <ol className="space-y-10">
        {visibleEntries.map((entry) => (
          <li key={entry.date} className="relative pl-8">
            {/* Dot */}
            <span className="absolute left-0 top-1.5 flex h-3.5 w-3.5 items-center justify-center">
              <GitCommit className="h-3.5 w-3.5 text-muted-foreground" />
            </span>

            {/* Date + commit count */}
            <div className="flex items-baseline gap-2">
              <time
                dateTime={entry.date}
                className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
              >
                {formatDate(entry.date)}
              </time>
              <span className="rounded-full border border-border px-1.5 py-px text-[10px] text-muted-foreground/70">
                {entry.commitCount} commit{entry.commitCount !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Items */}
            <ul className="mt-2 space-y-1.5">
              {entry.visibleItems.map((item) => {
                const cat = CATEGORY_MAP[item.category];
                const Icon = cat.icon;
                return (
                  <li
                    key={item.text}
                    className="flex items-start gap-2 text-sm text-foreground"
                  >
                    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                    {item.text}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}
